import React, { useState } from "react";
import { D, st } from "../../constants";
import { CABLE_TYPES, AUDIO_SOURCES, VIDEO_SOURCES } from "./avLightingRates";

let nextRoomId = 1;
let nextRunId = 1;

function capitalizeFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function newRoom(n) {
  return {
    id: nextRoomId++,
    name: `Room ${n}`,
    speakers: 0,
    speakerType: "In-ceiling",
    audioSource: AUDIO_SOURCES[0],
    displayCount: 0,
    displayType: "TV",
    videoSource: VIDEO_SOURCES[0],
  };
}
function newRun(roomId) {
  return { id: nextRunId++, roomId, from: "", to: "", cableType: CABLE_TYPES[0].id, lengthFt: "", purpose: "" };
}

export default function AVPlanner({ initialData, onChange }) {
  const [rooms, setRooms] = useState(() => (initialData?.rooms?.length ? initialData.rooms : [newRoom(1)]));
  const [wireRuns, setWireRuns] = useState(() => initialData?.wireRuns || []);

  React.useEffect(() => {
    onChange?.({ rooms, wireRuns });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, wireRuns]);

  const addRoom = () => setRooms((prev) => [...prev, newRoom(prev.length + 1)]);
  const removeRoom = (id) => {
    setRooms((prev) => prev.filter((r) => r.id !== id));
    setWireRuns((prev) => prev.filter((r) => r.roomId !== id));
  };
  const updateRoom = (id, patch) => setRooms((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const addRun = (roomId) => setWireRuns((prev) => [...prev, newRun(roomId)]);
  const removeRun = (id) => setWireRuns((prev) => prev.filter((r) => r.id !== id));
  const updateRun = (id, patch) => setWireRuns((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const generateSpeakerRuns = (room) => {
    const count = Math.max(0, Number(room.speakers) || 0);
    const runs = Array.from({ length: count }, (_, i) => ({
      id: nextRunId++, roomId: room.id, from: "", to: `${room.name} ${i + 1}`, cableType: "speaker-16-2", lengthFt: "", purpose: "Speaker",
    }));
    setWireRuns((prev) => [...prev, ...runs]);
  };
  const generateDisplayRuns = (room) => {
    const count = Math.max(0, Number(room.displayCount) || 0);
    const runs = Array.from({ length: count }, (_, i) => ({
      id: nextRunId++, roomId: room.id, from: "", to: `${room.name} ${i + 1}`, cableType: "hdbaset-cat6", lengthFt: "", purpose: "Video",
    }));
    setWireRuns((prev) => [...prev, ...runs]);
  };

  return (
    <div>
      <div style={st.card}>
        <div style={st.h2}>AV Planner</div>
        <div style={{ fontSize: 13, color: D.muted }}>
          Everything for a room — audio, video, and its low-voltage wire runs — lives together in that room's card.
        </div>
      </div>

      {rooms.map((room) => {
        const roomRuns = wireRuns.filter((r) => r.roomId === room.id);
        return (
          <div key={room.id} style={st.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <input value={room.name} onChange={(e) => updateRoom(room.id, { name: capitalizeFirst(e.target.value) })} style={{ ...st.input, fontWeight: 600, fontSize: 15, width: 200 }} />
              {rooms.length > 1 && <button style={st.btn("danger")} onClick={() => removeRoom(room.id)}>Remove Room</button>}
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: D.text, marginBottom: 8 }}>Audio</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 12, marginBottom: 16 }}>
              <label>
                <span style={st.label}>Speakers</span>
                <input type="number" min="0" value={room.speakers} onChange={(e) => updateRoom(room.id, { speakers: e.target.value })} style={st.input} />
              </label>
              <label>
                <span style={st.label}>Speaker type</span>
                <select value={room.speakerType} onChange={(e) => updateRoom(room.id, { speakerType: e.target.value })} style={st.input}>
                  <option>In-ceiling</option>
                  <option>In-wall</option>
                  <option>Outdoor</option>
                  <option>Bookshelf/Freestanding</option>
                  <option>Soundbar</option>
                </select>
              </label>
              <label>
                <span style={st.label}>Source</span>
                <select value={room.audioSource} onChange={(e) => updateRoom(room.id, { audioSource: e.target.value })} style={st.input}>
                  {AUDIO_SOURCES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: D.text, marginBottom: 8 }}>Video</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 12, marginBottom: 16 }}>
              <label>
                <span style={st.label}>Displays</span>
                <input type="number" min="0" value={room.displayCount} onChange={(e) => updateRoom(room.id, { displayCount: e.target.value })} style={st.input} />
              </label>
              <label>
                <span style={st.label}>Display type</span>
                <select value={room.displayType} onChange={(e) => updateRoom(room.id, { displayType: e.target.value })} style={st.input}>
                  <option>TV</option>
                  <option>Projector</option>
                  <option>Outdoor TV</option>
                </select>
              </label>
              <label>
                <span style={st.label}>Source</span>
                <select value={room.videoSource} onChange={(e) => updateRoom(room.id, { videoSource: e.target.value })} style={st.input}>
                  {VIDEO_SOURCES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: D.text, marginBottom: 8 }}>Wire Runs</div>
            <table style={{ ...st.table, marginBottom: 10 }}>
              <thead>
                <tr>
                  <th style={st.th}>From</th>
                  <th style={st.th}>To</th>
                  <th style={st.th}>Cable Type</th>
                  <th style={st.th}>Length (ft)</th>
                  <th style={st.th}>Purpose</th>
                  <th style={st.th}></th>
                </tr>
              </thead>
              <tbody>
                {roomRuns.map((run) => (
                  <tr key={run.id}>
                    <td style={st.td}><input value={run.from} onChange={(e) => updateRun(run.id, { from: capitalizeFirst(e.target.value) })} style={st.input} placeholder="e.g. Rack" /></td>
                    <td style={st.td}><input value={run.to} onChange={(e) => updateRun(run.id, { to: capitalizeFirst(e.target.value) })} style={st.input} placeholder={`e.g. ${room.name} 1`} /></td>
                    <td style={st.td}>
                      <select value={run.cableType} onChange={(e) => updateRun(run.id, { cableType: e.target.value })} style={st.input}>
                        {CABLE_TYPES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                      </select>
                    </td>
                    <td style={st.td}><input type="number" min="0" value={run.lengthFt} onChange={(e) => updateRun(run.id, { lengthFt: e.target.value })} style={{ ...st.input, width: 90 }} /></td>
                    <td style={st.td}><input value={run.purpose} onChange={(e) => updateRun(run.id, { purpose: capitalizeFirst(e.target.value) })} style={st.input} placeholder="e.g. Ceiling speakers" /></td>
                    <td style={st.td}><button style={{ ...st.btn("danger"), fontSize: 11, padding: "3px 8px" }} onClick={() => removeRun(run.id)}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={st.btn()} onClick={() => addRun(room.id)}>+ Add wire run</button>
              {Number(room.speakers) > 0 && <button style={st.btn()} onClick={() => generateSpeakerRuns(room)}>+ Generate speaker runs</button>}
              {Number(room.displayCount) > 0 && <button style={st.btn()} onClick={() => generateDisplayRuns(room)}>+ Generate display runs</button>}
            </div>
          </div>
        );
      })}
      <button style={st.btn("primary")} onClick={addRoom}>+ Add room</button>
    </div>
  );
}
